// src/components/Forum/PostCard.jsx
import React, { useState, useEffect } from 'react';
import ReactDOM from 'react-dom';
import { addInterestToPost } from '../../services/forumApi';
import { getJwtToken } from '../../utils/auth';
import { toast } from 'react-toastify';
import { MessageCircle, MapPin, Clock, Phone } from 'lucide-react';
import { fetchImages } from '../../storageService';
import './PostCard.css';

const PostCard = ({ post, isProvider = false, isActiveProvider = false, onInterestAdded }) => {
    const [isAddingInterest, setIsAddingInterest] = useState(false);
    const [hasInterest, setHasInterest] = useState(false);

    // Estados para modal de imágenes
    const [isImageModalOpen, setIsImageModalOpen] = useState(false);
    const [currentImageIndex, setCurrentImageIndex] = useState(0);
    const [postImages, setPostImages] = useState([]);
    const [imagesLoaded, setImagesLoaded] = useState(false);

    // Función para manejar Timestamps de Google que pueden venir como objeto {seconds, nanos}
    const parseTimestamp = (timestamp) => {
        if (!timestamp) return null;

        // Si es un objeto con seconds y nanos (Timestamp de Google)
        if (timestamp && typeof timestamp === 'object' && timestamp.seconds !== undefined) {
            // Convertir seconds a milisegundos
            return new Date(timestamp.seconds * 1000);
        }

        // Si es una string o número, intentar parsearlo normalmente
        if (typeof timestamp === 'string' || typeof timestamp === 'number') {
            const date = new Date(timestamp);
            return isNaN(date.getTime()) ? null : date;
        }

        return null;
    };

    const formatDate = (dateValue) => {
        if (!dateValue) {
            return 'Sin fecha';
        }

        try {
            const date = parseTimestamp(dateValue);
            if (!date) {
                return 'Sin fecha';
            }

            return date.toLocaleDateString('es-AR', {
                day: 'numeric',
                month: 'short',
                hour: '2-digit',
                minute: '2-digit',
                hour12: false // Formato 24 horas
            });
        } catch (error) {
            console.error('Error formatting date:', error);
            return 'Sin fecha';
        }
    };

    const getPostDate = (post) => {
        // Log temporal para debuggear la estructura de fechas del backend
        console.log('🔍 Debug fecha del post:', {
            postId: post.id,
            createdAt: post.createdAt,
            createdDate: post.createdDate,
            timestamp: post.timestamp,
            created: post.created,
            date: post.date
        });

        // Intentar obtener la fecha de varios campos posibles
        const possibleDateFields = [
            post.createdAt,
            post.createdDate,
            post.publishedAt,
            post.timestamp,
            post.created,
            post.date
        ];

        for (const dateField of possibleDateFields) {
            if (dateField) {
                console.log(`✅ Campo de fecha encontrado:`, dateField);
                const formattedDate = formatDate(dateField);
                if (formattedDate !== 'Sin fecha') {
                    console.log(`✅ Fecha formateada:`, formattedDate);
                    return formattedDate;
                }
            }
        }

        // Si no hay fecha disponible, mostrar "Reciente" como fallback
        console.log('⚠️ No se encontró fecha válida, usando "Reciente"');
        return 'Reciente';
    };

    const getStatusColor = (status) => {
        switch (status) {
            case 'ACTIVE': return '#28a745';
            case 'PAUSED': return '#ffc107';
            case 'RESOLVED': return '#17a2b8';
            case 'CLOSED': return '#6c757d';
            case 'EXPIRED': return '#6c757d';
            case 'ASSIGNED': return '#ffc107';
            case 'CANCELLED': return '#dc3545';
            default: return '#28a745';
        }
    };

    const getStatusText = (status) => {
        switch (status) {
            case 'ACTIVE': return 'Activo';
            case 'PAUSED': return 'Pausado';
            case 'RESOLVED': return 'Resuelto';
            case 'CLOSED': return 'Cerrado';
            case 'EXPIRED': return 'Expirado';
            case 'ASSIGNED': return 'Asignado';
            case 'CANCELLED': return 'Cancelado';
            default: return 'Activo';
        }
    };

    const buildWhatsAppUrl = () => {
        if (!post.authorPhone) return null;
        const phoneNumber = post.authorPhone.replace(/[\s\-\(\)]/g, '');

        let postContent = '';
        if (post.title && post.title.trim()) {
            postContent = `"${post.title.trim()}"`;
        } else if (post.description) {
            postContent = post.description.length > 100
                ? `"${post.description.substring(0, 100)}..."`
                : `"${post.description}"`;
        } else {
            postContent = 'tu post';
        }

        const message = encodeURIComponent(
            `Hola! Vi tu post en CONTACTAME ${postContent} y me interesa ayudarte.`
        );
        return `https://wa.me/${phoneNumber}?text=${message}`;
    };

    // Click "Contactar" (proveedor): registra interés en el backend y abre WhatsApp.
    // Si el interés falla por validación (categoría/rate limit/duplicado/post no activo),
    // se muestra el motivo y NO se abre WhatsApp.
    const handleContact = async () => {
        if (!isProvider) return;
        // Solo proveedores con perfil ACTIVO pueden contactar.
        if (!isActiveProvider) {
            toast.info('Activá tu perfil de proveedor para poder contactar.');
            return;
        }
        if (isAddingInterest) return;

        if (!post.authorPhone) {
            toast.error('Este post no tiene teléfono de contacto');
            return;
        }

        setIsAddingInterest(true);
        try {
            const token = await getJwtToken();
            if (!token) {
                toast.error('Debes iniciar sesión');
                return;
            }

            if (!hasInterest) {
                try {
                    // El backend resuelve el providerId desde el token (providerRef del User),
                    // no hace falta mandarlo desde acá.
                    await addInterestToPost(post.id, {
                        message: 'Interesado en brindar el servicio'
                    });

                    setHasInterest(true);
                    if (onInterestAdded) onInterestAdded(post.id);
                } catch (error) {
                    console.error('Error adding interest:', error);
                    const status = error.response?.status;
                    const backendMsg = error.response?.data?.message;

                    if (status === 409) {
                        // Ya había mostrado interés: continuar igual y abrir WhatsApp
                        setHasInterest(true);
                    } else if (status === 429) {
                        toast.error(backendMsg || 'Estás contactando demasiados posts en poco tiempo. Probá más tarde.');
                        return;
                    } else if (status === 403) {
                        toast.error(backendMsg || 'Solo proveedores de la categoría del post pueden contactar.');
                        return;
                    } else if (status === 400) {
                        toast.error(backendMsg || 'No se pudo registrar el contacto');
                        return;
                    } else {
                        toast.error('Error al registrar el contacto. Intentá de nuevo.');
                        return;
                    }
                }
            }

            const url = buildWhatsAppUrl();
            if (url) window.open(url, '_blank');
        } finally {
            setIsAddingInterest(false);
        }
    };

    // Cargar imágenes del post
    useEffect(() => {
        const loadPostImages = async () => {
            if (post.images && post.images.length > 0 && !imagesLoaded) {
                try {
                    // Usar fetchImages con el array de paths
                    const images = await fetchImages(null, post.images);
                    setPostImages(images.workPhotos || []);
                    setImagesLoaded(true);
                } catch (error) {
                    console.error('Error loading post images:', error);
                }
            }
        };

        loadPostImages();
    }, [post.images, imagesLoaded]);

    // Efecto para manejar navegación por teclado en el modal
    useEffect(() => {
        const handleKeyPress = (event) => {
            if (!isImageModalOpen) return;

            switch (event.key) {
                case 'Escape':
                    closeImageModal();
                    break;
                case 'ArrowLeft':
                    if (postImages.length > 1) {
                        prevImage();
                    }
                    break;
                case 'ArrowRight':
                    if (postImages.length > 1) {
                        nextImage();
                    }
                    break;
                default:
                    break;
            }
        };

        if (isImageModalOpen) {
            document.addEventListener('keydown', handleKeyPress);
        }

        return () => {
            document.removeEventListener('keydown', handleKeyPress);
        };
    }, [isImageModalOpen, postImages.length, currentImageIndex]);

    // Función para abrir modal de imagen
    const openImageModal = (index) => {
        setCurrentImageIndex(index);
        setIsImageModalOpen(true);
    };

    // Función para cerrar modal de imagen
    const closeImageModal = () => {
        setIsImageModalOpen(false);
        setCurrentImageIndex(0);
    };

    // Navegación entre imágenes
    const nextImage = () => {
        setCurrentImageIndex((prevIndex) => (prevIndex + 1) % postImages.length);
    };

    const prevImage = () => {
        setCurrentImageIndex((prevIndex) => (prevIndex - 1 + postImages.length) % postImages.length);
    };

    return (
        <div className="forum-post-card-container">
            <div className="forum-post-card-header">
                <div className="forum-post-card-meta">
                    <div className="forum-post-status-location-container">
                        <span
                            className="forum-post-status-badge"
                            style={{ backgroundColor: getStatusColor(post.status) }}
                        >
                            {getStatusText(post.status)}
                        </span>
                        {post.serviceLocationId && (
                            <div className="forum-post-location-info-header">
                                <MapPin size={12} />
                                <span>{post.serviceLocationId}</span>
                            </div>
                        )}
                    </div>
                    <div className="forum-post-date-display">
                        <Clock size={14} />
                        {getPostDate(post)}
                    </div>
                </div>
            </div>

            <div className="forum-post-content">
                {post.title && (
                    <h3 className="forum-post-title-text">{post.title}</h3>
                )}

                <p className="forum-post-description-text">
                    {post.description}
                </p>

                {/* Imágenes del post */}
                {postImages.length > 0 && (
                    <div className="forum-post-images-section">
                        <div className="forum-post-images-container">
                            {postImages.map((imageUrl, index) => (
                                <img
                                    key={index}
                                    src={imageUrl || "/placeholder.svg"}
                                    alt={`Imagen del post ${index + 1}`}
                                    className="forum-post-image"
                                    onClick={() => openImageModal(index)}
                                />
                            ))}
                        </div>
                    </div>
                )}

                <div className="forum-post-details-container">

                    <div className="forum-post-interests-info">
                        <MessageCircle size={14} />
                        <span>{post.totalInterested || 0} interesado{(post.totalInterested || 0) !== 1 ? 's' : ''}</span>
                    </div>

                    {post.preferredVisitTime && (
                        <div className="forum-post-visit-time-info" title="Horario preferido de contacto">
                            <Clock size={14} />
                            <span>{post.preferredVisitTime}</span>
                        </div>
                    )}
                </div>

                {/* Categorías ocultas según solicitud del usuario */}
            </div>

            {isProvider && (() => {
                // El botón se muestra siempre que seas proveedor. Si no podés contactar,
                // va grisado y al clickearlo se explica el motivo. null = podés contactar.
                let blockReason = null;
                if (post.status !== 'ACTIVE') {
                    blockReason = 'Este post no está aceptando nuevos contactos en este momento.';
                } else if (!post.authorPhone) {
                    blockReason = 'Este post no tiene un teléfono de contacto.';
                } else if (!isActiveProvider) {
                    blockReason = 'Tu perfil de proveedor debe estar activo para poder contactar a los autores de los posts.';
                }
                const isBlocked = blockReason !== null;
                return (
                <div className="forum-post-actions-container">
                    <button
                        className={`forum-post-btn forum-post-btn-whatsapp ${isBlocked ? 'forum-post-btn-disabled' : (hasInterest ? 'forum-post-interested' : '')}`}
                        onClick={isBlocked ? () => toast.info(blockReason) : handleContact}
                        disabled={isAddingInterest}
                        aria-disabled={isBlocked}
                        title={isBlocked ? blockReason : 'Contactar al autor del post por WhatsApp'}
                    >
                        <svg width="16" height="16" viewBox="0 0 24 24" fill="#ffffff">
                            <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893A11.821 11.821 0 0020.885 3.787"/>
                        </svg>
                        {isAddingInterest ? 'Enviando...' : 'Contactar'}
                    </button>
                </div>
                );
            })()}

            {/* Modal de imágenes usando portal */}
            {isImageModalOpen && postImages.length > 0 && ReactDOM.createPortal(
                <div className="image-modal-overlay" onClick={closeImageModal}>
                    <div className="image-modal-content" onClick={(e) => e.stopPropagation()}>
                        <span className="image-modal-close" onClick={closeImageModal}>&times;</span>
                        {postImages.length > 1 && (
                            <div className="image-modal-navigation">
                                <button className="image-modal-prev" onClick={prevImage}>
                                    ‹
                                </button>
                                <button className="image-modal-next" onClick={nextImage}>
                                    ›
                                </button>
                            </div>
                        )}
                        <img
                            src={postImages[currentImageIndex] || "/placeholder.svg"}
                            alt={`Imagen del post ${currentImageIndex + 1}`}
                            className="image-modal-image"
                        />
                        {postImages.length > 1 && (
                            <div className="image-modal-counter">
                                {currentImageIndex + 1} / {postImages.length}
                            </div>
                        )}
                    </div>
                </div>,
                document.body
            )}
        </div>
    );
};

export default PostCard;
